import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Ban, RotateCcw, Undo2, Link2, Search } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import StatusBadge from "@/components/StatusBadge";
import RampCodingPanel from "@/components/RampCodingPanel";
import DocumentsPanel from "@/components/DocumentsPanel";
import { useAuth } from "@/contexts/AuthContext";
import { CARD_CODE_ROLES } from "@/constants/roles";
import { useDebounced } from "@/hooks/usePagedSearch";
import { fmtDate } from "@/lib/utils";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

// Two ways a Ramp charge is handled. Where Ramp posts to QuickBooks (V1,
// rampPosts), it is coded right here — category + job, memo, receipt — and
// needs no Expense Log entry. Otherwise it is only ever matched to the
// Expense Log entry logged with payment method "Ramp", which carries job
// cost and goes to Accounting — matching never creates either.
export default function CardTransactionDrawer({ txnId, open, onOpenChange, onChanged, canManage = true, rampPosts = false }) {
  const { user } = useAuth();
  const canCode = CARD_CODE_ROLES.includes(user?.role);
  const [txn, setTxn] = useState(null);
  const [candidates, setCandidates] = useState(null);
  const [search, setSearch] = useState("");
  const [showExclude, setShowExclude] = useState(false);
  const [excludeReason, setExcludeReason] = useState("");
  const [saving, setSaving] = useState(false);
  const q = useDebounced(search);

  const load = () => api.get(`/contractor/card-transactions/${txnId}`).then((r) => setTxn(r.data));

  useEffect(() => {
    if (!open || !txnId) return;
    setTxn(null); setCandidates(null); setSearch("");
    setShowExclude(false); setExcludeReason("");
    load();
  }, [open, txnId]); // eslint-disable-line react-hooks/exhaustive-deps

  const unmatched = txn?.status === "unmatched";
  useEffect(() => {
    if (!open || !txnId || !unmatched || !canManage) return;
    api.get(`/contractor/card-transactions/${txnId}/candidates`, { params: { q } }).then((r) => setCandidates(r.data)).catch(() => setCandidates([]));
  }, [open, txnId, unmatched, q, canManage]);

  const run = async (fn, success, fallback) => {
    setSaving(true);
    try {
      const { data } = await fn();
      setTxn(data);
      toast.success(success);
      onChanged?.();
      return true;
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => x.message).join(" ") : d || fallback);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const match = (c) => run(() => api.post(`/contractor/card-transactions/${txnId}/match`, c.kind === "po" ? { po_id: c.id } : { expense_id: c.id }), `Matched to ${c.number}`, "Failed to match");
  const unmatch = () => run(() => api.post(`/contractor/card-transactions/${txnId}/unmatch`), "Unmatched", "Failed to unmatch");
  const resendReceipts = () => run(() => api.post(`/contractor/card-transactions/${txnId}/entry-receipts/push`), "Receipts sent to Ramp again", "Couldn't send the receipts to Ramp");
  const restore = () => run(() => api.post(`/contractor/card-transactions/${txnId}/restore`), "Restored to unmatched", "Failed to restore");
  const exclude = async () => {
    if (!excludeReason.trim()) { toast.error("A reason is required"); return; }
    if (await run(() => api.post(`/contractor/card-transactions/${txnId}/exclude`, { reason: excludeReason.trim() }), "Excluded", "Failed to exclude")) {
      setShowExclude(false); setExcludeReason("");
    }
  };

  const expense = txn?.matched_expense;
  const po = txn?.matched_po;
  const matchedItem = expense || po;
  const entryReceipts = txn?.entry_receipts || [];
  const receiptsFailed = entryReceipts.filter((r) => r.ramp && !r.ramp.ok);
  const receiptsSent = entryReceipts.filter((r) => r.ramp?.ok).length;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto p-0" data-testid="card-txn-drawer">
        {!txn ? (
          <div className="p-6 text-slate-400 text-sm">Loading…</div>
        ) : (
          <div className="p-6">
            <SheetHeader className="text-left">
              <SheetTitle className="flex items-center gap-2 flex-wrap">{txn.merchant} <StatusBadge status={txn.status} /></SheetTitle>
            </SheetHeader>

            <div className="mt-4 space-y-2.5 text-[13px]">
              <div className="flex justify-between gap-3"><span className="text-slate-500">Date</span><span>{fmtDate(txn.txn_date)}</span></div>
              <div className="flex justify-between gap-3"><span className="text-slate-500">Cardholder</span><span>{txn.cardholder || "—"}{txn.card_last4 ? ` ···· ${txn.card_last4}` : ""}</span></div>
              <div className="flex justify-between gap-3"><span className="text-slate-500">Ramp transaction ID</span><span className="mono text-[12px]">{txn.external_id}</span></div>
              {(txn.job || txn.purchasing_context) && (
                <div className="flex justify-between gap-3" data-testid="card-txn-job"><span className="text-slate-500">Job</span><span>{txn.job ? `${txn.job.code} — ${txn.job.name}` : txn.purchasing_context.name}</span></div>
              )}
              {txn.category && <div className="flex justify-between gap-3"><span className="text-slate-500">Category</span><span>{txn.category.code} {txn.category.name}</span></div>}
              <div className="flex justify-between gap-3 border-t border-slate-100 pt-2"><span className="text-slate-500 font-semibold">Amount</span><span className="mono font-bold">{fmt(txn.amount)}</span></div>
            </div>

            {txn.double_posted && (
              <div className="mt-5 text-[12.5px] text-red-800 bg-red-50 border border-red-100 rounded-lg px-3.5 py-3" data-testid="card-txn-double-posted">
                <span className="font-semibold">In QuickBooks twice.</span> Ramp posted this charge to QuickBooks after ScapeSource posted the matched
                Expense Log entry. Delete Ramp's copy in QuickBooks, and turn off card-transaction sync to QuickBooks in Ramp.
              </div>
            )}

            {rampPosts && canCode && (txn.status === "unmatched" || txn.status === "coded") && (
              <RampCodingPanel txn={txn} canManage={canManage} onChange={(next) => { setTxn(next); onChanged?.(); }} />
            )}

            {txn.status === "matched" && (
              <div className="mt-5 rounded-lg border border-emerald-100 bg-emerald-50 px-4 py-3 text-[13px]" data-testid="card-txn-matched">
                <div className="text-[11px] uppercase tracking-wide text-emerald-700 font-bold mb-1">{po ? "Matched purchase order" : "Matched Expense Log entry"}</div>
                {matchedItem ? (
                  <>
                    <Link to={po ? `/contractor/purchase-orders/${po.id}` : `/contractor/expense-log/${expense.id}`} className="mono font-semibold text-sky-700 hover:underline">{matchedItem.number}</Link>
                    <div className="text-slate-600 mt-0.5">{matchedItem.vendor || "—"} · {matchedItem.purchased_by_name || "—"} · {fmtDate(matchedItem.purchase_date)} · <span className="mono">{fmt(matchedItem.total)}</span></div>
                    {!matchedItem.amount_matches && <div className="text-amber-700 mt-1">The amounts differ by {fmt(Math.abs(matchedItem.total - txn.amount))}.</div>}
                  </>
                ) : <div className="text-slate-600">The matched entry no longer exists.</div>}
                <div className="text-[11.5px] text-slate-500 mt-1.5">{txn.matched_by_name}{txn.matched_at ? ` · ${fmtDate(txn.matched_at)}` : ""}</div>
                {matchedItem && (
                  <div className="mt-3 pt-3 border-t border-emerald-100" data-testid="card-txn-entry-receipts">
                    <DocumentsPanel entityType={po ? "purchase_order" : "outside_purchase"} entityId={matchedItem.id} title="Receipts on this entry" compact readOnly />
                    {entryReceipts.length === 0 && (
                      <div className="text-[12px] text-slate-500 mt-1.5">No receipt on the entry yet. Attach one there and it shows here and goes to Ramp.</div>
                    )}
                    {receiptsSent > 0 && <div className="text-[12px] text-emerald-800 mt-1.5">{receiptsSent} receipt{receiptsSent === 1 ? "" : "s"} sent to Ramp.</div>}
                    {receiptsFailed.map((r) => (
                      <div key={r.id} className="text-[12px] text-red-700 bg-red-50 border border-red-100 rounded-lg px-2.5 py-2 mt-1.5">{r.filename} didn't reach Ramp — {r.ramp.error}</div>
                    ))}
                    {canManage && receiptsFailed.length > 0 && (
                      <button disabled={saving} onClick={resendReceipts} data-testid="resend-entry-receipts" className="mt-2 h-8 px-3 rounded-lg border border-slate-200 bg-white text-[12px] font-semibold hover:bg-slate-50 disabled:opacity-60">
                        Send to Ramp again
                      </button>
                    )}
                  </div>
                )}
                {canManage && (
                  <button disabled={saving} onClick={unmatch} data-testid="unmatch-button" className="mt-3 h-9 px-3.5 rounded-lg border border-slate-200 bg-white text-[12.5px] font-semibold hover:bg-slate-50 flex items-center gap-1.5 disabled:opacity-60">
                    <Undo2 size={13} /> Unmatch
                  </button>
                )}
              </div>
            )}

            {unmatched && canManage && (
              <div className="mt-5" data-testid="card-txn-candidates">
                <div className="text-[11px] uppercase tracking-wide text-slate-400 font-bold mb-1">{rampPosts ? "Or match to an Expense Log entry or PO paid with Ramp" : "Match to an Expense Log entry or PO paid with Ramp"}</div>
                <p className="text-[12px] text-slate-500 mb-2.5">Entries and POs with the same amount are listed first, closest date first. Search to find any other unmatched Ramp entry or PO.</p>
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search PO #, vendor, purchaser…" data-testid="candidate-search"
                    className="w-full h-9 pl-9 pr-3 rounded-lg border border-slate-200 text-[12.5px]" />
                </div>
                <div className="mt-3 space-y-2">
                  {candidates === null && <div className="text-[12.5px] text-slate-400">Loading…</div>}
                  {candidates?.length === 0 && (
                    <div className="text-[12.5px] text-slate-500 bg-slate-50 border border-slate-100 rounded-lg px-3.5 py-3">
                      {q ? "No unmatched Ramp entries or POs match." : "No unmatched Expense Log entry or PO paid with Ramp has this amount. Ask the purchaser to log it with payment method Ramp (or set the PO to pay with Ramp), or search for it."}
                    </div>
                  )}
                  {candidates?.map((c) => (
                    <div key={c.id} className="flex items-center justify-between gap-3 border border-slate-200 rounded-lg px-3 py-2.5 text-[12.5px]" data-testid={`candidate-${c.id}`}>
                      <div className="min-w-0">
                        <div className="mono font-semibold flex items-center gap-1.5">{c.number}{c.kind === "po" && <span className="ss-pill ss-pill-sky text-[10px]">PO</span>}</div>
                        <div className="text-slate-500 truncate">{c.vendor || "—"} · {c.purchased_by_name || "—"} · {fmtDate(c.purchase_date)}{c.days_apart != null ? ` (${c.days_apart === 0 ? "same day" : `${c.days_apart}d apart`})` : ""}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`mono font-medium ${c.amount_matches ? "" : "text-amber-700"}`}>{fmt(c.total)}</span>
                        <button disabled={saving} onClick={() => match(c)} data-testid={`match-${c.id}`} className="h-8 px-3 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[12px] font-semibold flex items-center gap-1 disabled:opacity-60">
                          <Link2 size={12} /> Match
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-5 pt-4 border-t border-slate-100">
                  {!showExclude ? (
                    <button onClick={() => setShowExclude(true)} data-testid="exclude-button" className="h-9 px-3.5 rounded-lg border border-slate-200 text-slate-600 text-[12.5px] font-semibold hover:bg-slate-50 flex items-center gap-1.5">
                      <Ban size={13} /> Exclude (refund, fee, personal charge…)
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <textarea value={excludeReason} onChange={(e) => setExcludeReason(e.target.value)} rows={2} placeholder="Why this charge won't have an Expense Log entry" data-testid="exclude-reason-input"
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[12.5px]" />
                      <div className="flex gap-2">
                        <button disabled={saving} onClick={exclude} data-testid="confirm-exclude-button" className="h-9 px-4 rounded-lg bg-slate-800 text-white text-[12.5px] font-semibold disabled:opacity-60">Exclude</button>
                        <button onClick={() => { setShowExclude(false); setExcludeReason(""); }} className="h-9 px-4 rounded-lg border border-slate-200 text-[12.5px] font-semibold">Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {txn.status === "excluded" && (
              <div className="mt-5 text-[12.5px] text-slate-600 bg-slate-50 border border-slate-100 rounded-lg px-3.5 py-3" data-testid="card-txn-excluded">
                <span className="font-semibold">Excluded:</span> {txn.excluded_reason || "No reason recorded."}
                {canManage && (
                  <button disabled={saving} onClick={restore} data-testid="restore-button" className="mt-3 h-9 px-3.5 rounded-lg border border-slate-200 bg-white text-[12.5px] font-semibold hover:bg-slate-50 flex items-center gap-1.5 disabled:opacity-60">
                    <RotateCcw size={13} /> Restore
                  </button>
                )}
              </div>
            )}

            {txn.status === "assigned" && (
              <div className="mt-5 text-[12.5px] text-slate-600 bg-slate-50 border border-slate-100 rounded-lg px-3.5 py-3">
                Assigned to a job under the earlier card flow — it's handled in Accounting like before.
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
